// App Builder hub. Split-view editor: left = configuration tabs, right =
// live mobile preview. Auto-saves draft to localStorage; Save persists to
// server as draft; Publish promotes to the live config consumed by mobile.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  PageHeader,
  Badge,
} from '@erp/ui';
import type { BottomNavItem } from '@erp/app-builder';
import { Save, RotateCcw, Sparkles } from '@/components/icons';
import { useBuilderState } from './use-builder-state';
import { MobilePreview } from './MobilePreview';
import { IdentityTab } from './tabs/IdentityTab';
import { ThemeTab } from './tabs/ThemeTab';
import { BottomNavTab } from './tabs/BottomNavTab';
import { ModulesTab } from './tabs/ModulesTab';
import { ShortcutsTab } from './tabs/ShortcutsTab';
import { PagesTab } from './tabs/PagesTab';

export function AppBuilderPage() {
  const { t } = useTranslation();
  const state = useBuilderState();
  const [previewLang, setPreviewLang] = useState<'fr' | 'ar' | 'en'>('fr');
  const [previewScreen, setPreviewScreen] = useState<string | undefined>(undefined);

  if (state.loading) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        {t('common.loading', { defaultValue: 'Chargement…' })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      <PageHeader
        title={t('appBuilder.title', { defaultValue: 'App Builder' })}
        description={t('appBuilder.description', {
          defaultValue: 'Personnalisez l’apparence et la navigation de l’app mobile pour votre entreprise.',
        })}
        actions={
          <div className="flex items-center gap-2">
            {state.dirty ? (
              <Badge variant="outline" className="border-orange-500 text-orange-700">
                {t('appBuilder.unsaved', { defaultValue: 'Modifications non enregistrées' })}
              </Badge>
            ) : null}
            {state.published ? (
              <Badge variant="outline">
                v{state.published ? '1' : '0'} {t('appBuilder.published', { defaultValue: 'publié' })}
              </Badge>
            ) : null}
            <Button variant="outline" size="sm" onClick={() => state.resetToPublished()}>
              <RotateCcw size={16} />
              {t('appBuilder.reset', { defaultValue: 'Réinitialiser' })}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => state.save()}
              disabled={!state.dirty || state.isSaving || !!state.validationError}
            >
              <Save size={16} />
              {state.isSaving
                ? t('common.saving', { defaultValue: 'Enregistrement…' })
                : t('appBuilder.saveDraft', { defaultValue: 'Enregistrer brouillon' })}
            </Button>
            <Button
              size="sm"
              onClick={() => state.publish()}
              disabled={state.isPublishing || !!state.validationError}
            >
              <Sparkles size={16} />
              {state.isPublishing
                ? t('common.publishing', { defaultValue: 'Publication…' })
                : t('appBuilder.publish', { defaultValue: 'Publier' })}
            </Button>
          </div>
        }
      />

      {state.validationError ? (
        <Card className="border-destructive">
          <CardContent className="py-3 text-sm text-destructive">
            {t('appBuilder.validationError', {
              defaultValue: 'Erreurs de validation',
            })}
            : {state.validationError}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(360px,400px)] gap-6">
        {/* LEFT — editor */}
        <Card>
          <CardContent className="p-0">
            <Tabs defaultValue="identity">
              <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0 h-auto">
                <TabTrigger value="identity">
                  {t('appBuilder.tabs.identity', { defaultValue: 'Identité' })}
                </TabTrigger>
                <TabTrigger value="theme">
                  {t('appBuilder.tabs.theme', { defaultValue: 'Thème' })}
                </TabTrigger>
                <TabTrigger value="bottomNav">
                  {t('appBuilder.tabs.bottomNav', { defaultValue: 'Navigation' })}
                </TabTrigger>
                <TabTrigger value="modules">
                  {t('appBuilder.tabs.modules', { defaultValue: 'Modules' })}
                </TabTrigger>
                <TabTrigger value="shortcuts">
                  {t('appBuilder.tabs.shortcuts', { defaultValue: 'Raccourcis' })}
                </TabTrigger>
                <TabTrigger value="pages">
                  {t('appBuilder.tabs.pages', { defaultValue: 'Écrans' })}
                </TabTrigger>
              </TabsList>
              <div className="p-6">
                <TabsContent value="identity">
                  <IdentityTab value={state.draft.identity} onChange={(identity) => state.setDraft({ ...state.draft, identity })} />
                </TabsContent>
                <TabsContent value="theme">
                  <ThemeTab value={state.draft.theme} onChange={(theme) => state.setDraft({ ...state.draft, theme })} />
                </TabsContent>
                <TabsContent value="bottomNav">
                  <BottomNavTab
                    value={state.draft.bottomNav}
                    onChange={(bottomNav) => state.setDraft({ ...state.draft, bottomNav })}
                    enabledModules={state.draft.modules}
                  />
                </TabsContent>
                <TabsContent value="modules">
                  <ModulesTab value={state.draft.modules} onChange={(modules) => state.setDraft({ ...state.draft, modules })} />
                </TabsContent>
                <TabsContent value="shortcuts">
                  <ShortcutsTab value={state.draft.shortcuts} onChange={(shortcuts) => state.setDraft({ ...state.draft, shortcuts })} />
                </TabsContent>
                <TabsContent value="pages">
                  <PagesTab
                    value={state.draft.screens ?? {}}
                    onChange={(screens) => state.setDraft({ ...state.draft, screens })}
                    enabledModules={state.draft.modules}
                  />
                </TabsContent>
              </div>
            </Tabs>
          </CardContent>
        </Card>

        {/* RIGHT — preview */}
        <div className="space-y-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between">
                <span>{t('appBuilder.preview', { defaultValue: 'Aperçu' })}</span>
                <div className="flex gap-1">
                  {(['fr', 'ar', 'en'] as const).map((l) => (
                    <button
                      key={l}
                      onClick={() => setPreviewLang(l)}
                      className={`px-2 py-1 text-xs rounded ${previewLang === l ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
                    >
                      {l.toUpperCase()}
                    </button>
                  ))}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-2 pb-6 flex flex-col items-center gap-3">
              <MobilePreview config={state.draft} lang={previewLang} activeScreenId={previewScreen} />
              <div className="flex flex-wrap gap-1 justify-center">
                {state.draft.bottomNav.map((b: BottomNavItem) => (
                  <button
                    key={b.id}
                    onClick={() => setPreviewScreen(b.destination)}
                    className={`px-2 py-1 text-[10px] rounded ${previewScreen === b.destination || (!previewScreen && b.destination === state.draft.bottomNav[0]?.destination) ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                  >
                    {(b.label as Record<string, string>)[previewLang] ?? b.label.fr}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function TabTrigger({ value, children }: { value: string; children: React.ReactNode }) {
  return (
    <TabsTrigger
      value={value}
      className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3"
    >
      {children}
    </TabsTrigger>
  );
}
